import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useWorkspaceContext } from '@/contexts/workspace/context';
import { useWorkspaceScope } from '@/contexts/workspace/useWorkspaceScope';
import { useMyCashPoolingRole } from '@/features/cash-pooling/cash-pooling-roles/hooks/useCashPoolingRoles';

import { CASH_POOLING_MEMBER, GATE } from '../model/gates';
import { opensGate } from '../model/moduleAccess';
import type { TaskRequirement, WidgetAccess } from '../model/types';
import { useHeldFor } from './useHeldFor';

interface TaskDto {
  id: number;
  parentId?: number | null;
}

export interface TaskAccess {
  /** False until the reader's tasks are known for the workspace on screen. */
  ready: boolean;
  access: WidgetAccess | undefined;
}

export const UNKNOWN_ACCESS: TaskAccess = Object.freeze({ ready: false, access: undefined });

/** Filled once per app by `TaskAccessProvider`; outside it nothing is known. */
export const TaskAccessContext = createContext<TaskAccess>(UNKNOWN_ACCESS);

export const useTaskAccess = (): TaskAccess => useContext(TaskAccessContext);

/** Whether this reader may open `gate` here; false until their tasks are known. */
export const useAccessTo = (gate: TaskRequirement): boolean => {
  const { access } = useTaskAccess();
  return access !== undefined && opensGate(gate, access);
};

/**
 * How long a scope that was never ready may stay so before the loaded tasks count as the screen's.
 *
 * A client switch starts from a ready scope and ends in a reload, so it gets no grace however
 * long it takes. A scope that never becomes ready is a backend whose session and workspaces
 * disagree on the client id (the demo mock does); waiting on it would leave the overview empty.
 */
export const SCOPE_SETTLE_MS = 3000;

// Closes the enabled tasks over their ancestors as the backend does: a child task opens its parent's endpoints.
const withAncestors = (tasks: TaskDto[], catalogue: TaskDto[]): Set<number> => {
  const parentOf = new Map<number, number>();
  [...catalogue, ...tasks].forEach((task) => {
    if (task.parentId != null && task.parentId !== task.id) parentOf.set(task.id, task.parentId);
  });
  const enabled = new Set<number>();
  tasks.forEach((task) => {
    let current: number | undefined = task.id;
    while (current !== undefined && !enabled.has(current)) {
      enabled.add(current);
      current = parentOf.get(current);
    }
  });
  return enabled;
};

/** What this reader may open here, from the tasks the workspace context already loads for the ability. */
export const useResolvedTaskAccess = (): TaskAccess => {
  const { ready: scopeReady } = useWorkspaceScope();
  const { workspace } = useWorkspaceContext() ?? {};
  const enabled: TaskDto[] | undefined = workspace?.tasks;
  const catalogue: TaskDto[] | undefined = workspace?.allTasks;
  // The tasks land in the same render as the workspace they were loaded for.
  const loaded = Boolean(workspace?.defaultWorkspace?.id);

  const [wasReady, setWasReady] = useState(scopeReady);
  useEffect(() => {
    if (scopeReady) setWasReady(true);
  }, [scopeReady]);
  const settled = useHeldFor(loaded && !scopeReady && !wasReady, SCOPE_SETTLE_MS);
  const ready = scopeReady || settled;

  const taskAccess = useMemo(
    () => (ready && enabled ? withAncestors(enabled, catalogue ?? []) : undefined),
    [ready, enabled, catalogue],
  );
  const asksMembership = taskAccess !== undefined && opensGate(GATE.bankTransfers, taskAccess);
  const membership = useMyCashPoolingRole(asksMembership);
  const membershipKnown = !asksMembership || membership.isSuccess || membership.isError;
  const isMember = asksMembership && Boolean(membership.data);

  return useMemo<TaskAccess>(() => {
    if (!taskAccess || !membershipKnown) return UNKNOWN_ACCESS;
    return { ready: true, access: isMember ? new Set([...taskAccess, CASH_POOLING_MEMBER]) : taskAccess };
  }, [taskAccess, membershipKnown, isMember]);
};
